import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import axios from 'axios';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getScheduleDate } from '@/store/selectors/schedule';

import PropTypes from 'prop-types';

import API from '@/apiRequest';
import OrderModalComponent from '@/components/SchedulePage/OrderModal/OrderModalComponent';
import { useAsyncService } from '@/contexts/AsyncServiceContext';
import { useUser } from '@/contexts/UserContext';
import {
  prependPendingOrder,
  deleteOrder,
  setSelectedOrder,
  setUnsaved,
  clearFilteredInspectorsNotifications,
  notifyFilteredInspectors,
} from '@/store/ducks/schedule';
import { showToast } from '@/store/ducks/toasts';

const OrderModalContainer = ({ orderAlreadyBeingEditedByAnotherUser }) => {
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  const [isInTimeline, setIsInTimeline] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [currentOrderHistoric, setCurrentOrderHistoric] = useState([]);
  const [annotationUpdate, setAnnotationUpdate] = useState(0);

  const user = useUser();

  const canShowAnnotation = user?.authRole === 'ROLE_FRANCHISEE';

  const date = useSelector(getScheduleDate);
  const orders = useSelector(state => state.schedule.orders);
  const selectedOrder = useSelector(state => state.schedule.selectedOrder);
  const ordersThatAreBeingEdited = useSelector(state => state.schedule.ordersThatAreBeingEdited);

  const intl = useIntl();
  const dispatch = useDispatch();

  const { emit } = useAsyncService();

  const me = JSON.parse(window.sessionStorage.getItem('me'));

  const handleEmitOrderEditingHasBeenFinished = useCallback(
    (orderId = null) => {
      const isMeEditingTheOrder = ordersThatAreBeingEdited.find(orderThatAreBeingEdited => {
        return (
          orderThatAreBeingEdited?.order?.order?.public_id === orderId &&
          orderThatAreBeingEdited?.sender?.id === me?.uniqueId
        );
      });

      if (orderId && isMeEditingTheOrder) {
        emit({
          data: {
            channel: `panel_${me.panelId}`,
            event: 'SCHEDULE.ORDER_EDITING_HAS_BEEN_FINISHED',
            data: {
              order: {
                order: {
                  public_id: orderId,
                },
              },
              sender: {
                id: me?.uniqueId,
                who: me?.name,
                when: format(new Date(), 'yyyy-MM-dd HH:mm'),
              },
            },
          },
        });
      }
    },
    [emit, me, ordersThatAreBeingEdited]
  );

  const handleEmitOrderIsBeingUpdatedEvent = useCallback(
    (order = null) => {
      if (order) {
        const isAlreadyBeenEditedByAnotherUser = ordersThatAreBeingEdited.find(orderThatAreBeingEdited => {
          return (
            orderThatAreBeingEdited?.order?.order?.public_id === order?.order?.public_id &&
            orderThatAreBeingEdited?.sender?.id !== me?.uniqueId
          );
        });

        if (!isAlreadyBeenEditedByAnotherUser) {
          emit({
            data: {
              channel: `panel_${me.panelId}`,
              event: 'SCHEDULE.ORDER_IS_BEING_UPDATED',
              data: {
                order,
                sender: {
                  id: me?.uniqueId,
                  who: me?.name,
                  when: format(new Date(), 'yyyy-MM-dd HH:mm'),
                },
              },
            },
          });
        }
      }
    },
    [emit, me, ordersThatAreBeingEdited]
  );

  useEffect(() => {
    const timelineOrder = orders.reduce(
      (acc, cur) => (cur.order.public_id === selectedOrder.orderId ? { ...cur } : acc),
      null
    );
    const source = API.CancelToken.source();

    setIsInTimeline(!!timelineOrder);

    const parseOrder = order => {
      const { form } = order;
      const { schedule } = form || {};

      return {
        order: {
          public_id: selectedOrder?.orderId,
          code: order?.code,
          type: form?.inspection_type,
          client: form?.clients?.client,
          keys_location: form?.accessInformation?.keysLocation || intl.formatMessage({ id: 'UNINFORMED' }),
          ...form?.additionals,
          ...form?.details,
          ...form?.address,
          address_id: order?.address_id,
          order_type_id: order?.order_type_id,
          access_information: form?.accessInformation,
          property_access: form?.propertyAccess,
          accompanied_inspection: form?.accompaniedInspection,
          attachments: form?.attachments,
          previous_inspection: form?.previous_inspection,
          building_type: form?.details?.building_type,
          allowed_at_date: form?.details?.allowed_at_date,
          furnished: form?.details?.furnished,
          modality: form?.additionals?.modality,
          urgency: form?.additionals?.urgency,
          created_at: order?.created_at?.date,
          details: form?.details?.details,
          annotation: order?.annotation,
          search_tokens: order?.search_tokens,
          status: order?.status,
          credits: order?.credits ?? (form?.details?.price ? Math.round(Number(form.details.price) * 100) : null),
        },
        schedule: {
          who: {
            inspector_id:
              selectedOrder?.inspectorId || timelineOrder?.schedule?.who?.inspector_id || schedule?.inspector_id || '',
          },
          when: {
            date: format(
              typeof (schedule?.when?.date || date) === 'string'
                ? parseISO(schedule?.when?.date || date)
                : schedule?.when?.date || date,
              'dd/MM/yyyy'
            ),
            start_time:
              selectedOrder?.startTime || timelineOrder?.schedule?.when?.start_time || schedule?.start_time || '',
            end_time: timelineOrder?.schedule?.when?.end_time || schedule?.end_time || '',
          },
          flextime: timelineOrder?.schedule?.flextime || schedule?.flextime || false,
          observation: timelineOrder?.schedule?.observation || schedule?.observation || '',
        },
      };
    };

    const fetchOrder = async () => {
      try {
        setBusy(true);

        if (
          selectedOrder?.orderId &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selectedOrder?.orderId)
        ) {
          const { data } = await API.get(`/view/orders/${selectedOrder.orderId}`, { cancelToken: source.token });
          const parsedOrder = parseOrder(data);
          setCurrentOrder(parsedOrder);
          dispatch(
            notifyFilteredInspectors({
              address_id: data?.address_id,
              order_type_id: data?.order_type_id,
            })
          );
        }
      } catch (err) {
        setError(true);

        if (axios.isCancel(err)) return;
      } finally {
        setBusy(false);
      }
    };

    const fetchOrderHistoric = async () => {
      try {
        if (selectedOrder?.orderId) {
          const { data } = await API.get(
            `/activity-log/orders/${selectedOrder.orderId}/actions?types[]=INSPECTOR_CHECKIN&types[]=INSPECTOR_CHECKOUT&types[]=INSPECTOR_DO_INSPECTION_LATER&types[]=INSPECTOR_DO_INSPECTION_NOW&types[]=INSPECTOR_GOING_TO_PICK_UP_KEYS&types[]=INSPECTOR_GOING_TO_RETURN_KEYS&types[]=INSPECTOR_PICK_UP_KEYS&types[]=INSPECTOR_RETURN_KEYS`
          );

          const formatedHistoric = data.map(d => {
            const itemDate = typeof d.created_at.date === 'string' ? parseISO(d.created_at.date) : d.created_at.date;

            return {
              ...d,
              humanDate: formatDistanceToNow(itemDate, { addSuffix: true, locale: ptBR }),
              date: format(itemDate, "dd/MM/yyyy 'às' HH:mm"),
            };
          });
          setCurrentOrderHistoric(formatedHistoric);
        }
      } catch (err) {
        console.log(err);
      }
    };

    fetchOrder();
    fetchOrderHistoric();

    return () => {
      if (source) source.cancel();
    };
  }, [date, dispatch, intl, orders, selectedOrder, annotationUpdate]);

  useEffect(() => {
    if (currentOrder) {
      handleEmitOrderIsBeingUpdatedEvent(currentOrder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder]);

  const unassign = async ({ order, schedule }) => {
    const orderPublicId = order?.public_id || order?.id;
    const mappedOrder = {
      ...order,
      id: orderPublicId,
      public_id: orderPublicId,
      identifier: order?.building_id || order?.identifier,
      credits: order?.credits ?? (order?.price ? Math.round(Number(order.price) * 100) : undefined),
      area: order?.area ? String(order?.area) : '',
    };

    dispatch(deleteOrder(orderPublicId));
    dispatch(
      prependPendingOrder({
        client: mappedOrder?.client,
        code: mappedOrder?.code,
        identifier: mappedOrder?.identifier,
        id: orderPublicId,
        public_id: orderPublicId,
        type: mappedOrder?.type,
        credits: mappedOrder?.credits,
        address_id: mappedOrder?.address_id,
        order_type_id: mappedOrder?.order_type_id,
        building_type: mappedOrder?.building_type,
        allowed_at_date: mappedOrder?.allowed_at_date,
        search_tokens: mappedOrder?.search_tokens,
        accompanied_inspection: mappedOrder?.accompanied_inspection,
        furnished: mappedOrder?.furnished,
        modality: mappedOrder?.modality,
        urgency: mappedOrder?.urgency,
        created_at: mappedOrder?.created_at,
        access_information: mappedOrder?.access_information,
        details: mappedOrder?.details,
        status: mappedOrder?.status,
        city: mappedOrder?.city,
        neighborhood: mappedOrder?.neighborhood,
        street: mappedOrder?.street,
        number: mappedOrder?.number,
        complement: mappedOrder?.complement,
        state: mappedOrder?.state,
        zipcode: mappedOrder?.zipcode,
      })
    );
    dispatch(setSelectedOrder(null));
    dispatch(setUnsaved(true));
    dispatch(
      showToast({
        type: 'success',
        title: intl.formatMessage({ id: 'SUCCESS' }),
        text: intl.formatMessage({ id: 'INSPECTOR_ORDER_UNASSIGNED_SUCCESS' }),
        duration: 3000,
      })
    );

    try {
      await API.post('/scheduler/sync', {
        channel: `panel_${me?.panelId}`,
        event: 'SCHEDULE.ORDER_UNASSIGNED',
        data: {
          order: mappedOrder,
          schedule,
          sender: {
            id: me?.uniqueId,
            who: me?.name,
            when: format(new Date(), 'yyyy-MM-dd HH:mm'),
          },
        },
      });
    } catch {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'UNABLE_SAVE_CALENDAR_DATA_ERROR_MESSAGE' }),
          duration: 3000,
        })
      );
    }
  };

  const closeModal = useCallback(() => {
    dispatch(setSelectedOrder(null));
    dispatch(clearFilteredInspectorsNotifications());

    if (currentOrder?.order?.public_id) {
      handleEmitOrderEditingHasBeenFinished(currentOrder?.order?.public_id);
    }
  }, [currentOrder, dispatch, handleEmitOrderEditingHasBeenFinished]);

  const openAttachment = useCallback(
    (remoteName, originalName, extension) => {
      API.get(`/orders/${selectedOrder?.orderId}/attachments`, {
        params: {
          original_name: originalName,
          remote_name: remoteName,
          extension,
        },
      }).then(res => {
        window.open(res.data?.url, '_blank');
      });
    },
    [selectedOrder]
  );

  const refreshHandler = () => {
    setAnnotationUpdate(annotationUpdate + 1);
  };

  return (
    <OrderModalComponent
      isBusy={busy}
      hasErrors={error}
      confirmRemove={confirmRemove}
      setConfirmRemove={setConfirmRemove}
      isInTimeline={isInTimeline}
      order={currentOrder?.order}
      historic={currentOrderHistoric}
      schedule={currentOrder?.schedule}
      closeModal={closeModal}
      openAttachment={openAttachment}
      unassign={unassign}
      handleEmitOrderEditingHasBeenFinished={handleEmitOrderEditingHasBeenFinished}
      orderAlreadyBeingEditedByAnotherUser={orderAlreadyBeingEditedByAnotherUser}
      canShowAnnotation={canShowAnnotation}
      refreshHandler={refreshHandler}
    />
  );
};

OrderModalContainer.propTypes = {
  orderAlreadyBeingEditedByAnotherUser: PropTypes.object,
};

OrderModalContainer.defaultProps = {
  orderAlreadyBeingEditedByAnotherUser: null,
};

export default OrderModalContainer;
